import { LoadingContent, useAnimatedList } from '../components/feedback';
import { addonKindsOf, modLoaderOf, velocitySupportOf } from '@mcpanel/core';
import { HeapFields } from '../components/HeapFields';
import { ResourceSpecSchema } from '@mcpanel/core';
import { ClientModTemplatesSection } from './ClientModTemplatesSection';
import {
  GameSettingsPatchSchema,
  defaultGameSettings,
  gameSettingsFromProperties,
  gameSettingsProperties,
} from '@mcpanel/core';
import { JvmSettingsSchema, effectiveJvm } from '@mcpanel/core';
import { JvmSettingsFields } from '../components/JvmSettingsFields';
import { GameSettingsFields } from '../components/GameSettingsFields';
import { PlacementFields } from '../components/PlacementFields';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api3 } from '../api/client-p3';
import type { SoftwareSpec } from '../api/types';
import type { Template, TemplateInput } from '../api/types-p3';
import { errorMessage, useApp } from '../app-context';
import { AddonSearch, addonWord, isPluginFamily, SourceTag } from '../components/AddonSearch';
import { SoftwarePicker } from '../components/SoftwarePicker';
import { Dialog, Empty, ErrorAlert } from '../components/ui';
import { formatGb, softwareText } from '../format';
import { usePoll } from '../hooks/usePoll';

const blank: TemplateInput = {
  name: '',
  weight: 'heavy',
  description: '',
  software: { family: 'paper', mcVersion: '' },
  resources: { cores: 4, memoryMb: 12288, heapMb: 9216, heapMinMb: 9216, dataDiskGb: 60 },
  mods: [],
  properties: {},
  proxied: true,
  tasks: [],
};

function propertiesText(properties: Record<string, string>): string {
  return Object.entries(properties)
    .map(([key, value]) => `${key}=${value}`)
    .join('\n');
}

function parseProperties(text: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const index = trimmed.indexOf('=');
    if (index > 0) result[trimmed.slice(0, index).trim()] = trimmed.slice(index + 1).trim();
  }
  return result;
}

function TemplateEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: Template | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify } = useApp();
  // 保存済みのテンプレートの id・builtin・updatedAt・revision は入力に含めない（API は入力の項目だけを受け付ける）
  const [form, setForm] = useState<TemplateInput>(() => {
    if (!initial) return blank;
    const { id: _id, builtin: _builtin, updatedAt: _updatedAt, revision: _revision, ...input } = initial;
    return input;
  });
  const [properties, setProperties] = useState(propertiesText(initial?.properties ?? {}));
  const [gameSettings, setGameSettings] = useState(
    initial
      ? gameSettingsFromProperties({
          ...initial.properties,
          ...(initial.gameSettings ? gameSettingsProperties(initial.gameSettings) : {}),
        })
      : defaultGameSettings,
  );
  const [mods, setMods] = useState<TemplateInput['mods']>(initial?.mods ?? []);
  const modList = useAnimatedList(mods, (mod) => `${mod.source}:${mod.project}`);
  const effectiveJvmInput = () => form.jvm ?? effectiveJvm(form);
  const family = form.software.family;
  const isModded = addonKindsOf(family).mods;
  const directOnly = !velocitySupportOf(family);
  const takesAddons = isModded || isPluginFamily(family);
  const word = addonWord(family);

  /** Mod とプラグインは互換が無いので、系統が変わったら選んだものを外す */
  const changeSoftware = (software: SoftwareSpec) => {
    const kind = (f: string) => JSON.stringify(addonKindsOf(f)) + ':' + (modLoaderOf(f) ?? '');
    if (kind(software.family) !== kind(family) && mods.length > 0) {
      setMods([]);
      notify('ソフトの系統が変わったので、選んだ Mod・プラグインを外しました');
    }
    setForm({ ...form, software });
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const input: TemplateInput = {
        ...form,
        jvm: JvmSettingsSchema.parse(effectiveJvmInput()),
        proxied: form.proxied && !directOnly,
        properties: parseProperties(properties),
        gameSettings: GameSettingsPatchSchema.parse(gameSettings),
        mods: takesAddons ? mods : [],
      };
      if (initial) await api3.updateTemplate(initial.id, input);
      else await api3.createTemplate(input);
      notify('テンプレートを保存しました');
      onSaved();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const setResource = (key: keyof TemplateInput['resources'], value: number) =>
    setForm({ ...form, resources: { ...form.resources, [key]: value } });

  return (
    <Dialog
      title={initial ? 'テンプレートを編集' : 'テンプレートを作成'}
      wide
      lockResource={initial ? { target: `templates:${initial.id}`, screen: 'template' } : undefined}
      revision={initial?.revision}
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onClose}>
            キャンセル
          </button>
          <button
            className="btn primary"
            form="template-form"
            disabled={
              !form.name.trim() ||
              !form.software.mcVersion ||
              !ResourceSpecSchema.safeParse(form.resources).success ||
              !JvmSettingsSchema.safeParse(effectiveJvmInput()).success
            }
          >
            保存
          </button>
        </>
      }
    >
      <form id="template-form" className="stack" style={{ gap: 14 }} onSubmit={(event) => void save(event)}>
        <div className="field">
          <label htmlFor="tpl-name">名前</label>
          <input
            id="tpl-name"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="tpl-desc">説明</label>
          <input
            id="tpl-desc"
            className="input"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <PlacementFields
          weight={form.weight ?? 'heavy'}
          preferredNode={form.preferredNode}
          onChange={(weight, preferredNode) => setForm({ ...form, weight, preferredNode })}
        />
        <SoftwarePicker value={form.software} onChange={changeSoftware} />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
          {(
            [
              ['cores', 'CPU コア'],
              ['memoryMb', 'VM メモリ (GB)'],
              ['dataDiskGb', 'ディスク (GB)'],
            ] as const
          ).map(([key, label]) => (
            <div className="field" key={key}>
              <label htmlFor={`tpl-${key}`}>{label}</label>
              <input
                id={`tpl-${key}`}
                className="input"
                type="number"
                min={1}
                value={key === 'memoryMb' ? form.resources[key] / 1024 : form.resources[key]}
                onChange={(e) =>
                  setResource(
                    key,
                    key === 'memoryMb' ? Math.round(Number(e.target.value) * 1024) : Number(e.target.value),
                  )
                }
              />
            </div>
          ))}
        </div>
        <HeapFields
          unit="GB"
          value={form.resources}
          onChange={(heap) => setForm({ ...form, resources: { ...form.resources, ...heap } })}
        />
        {!ResourceSpecSchema.safeParse(form.resources).success && (
          <div className="alert danger">ヒープは 512MB ≦ 最小 ≦ 最大 &lt; VM メモリにしてください。</div>
        )}
        {takesAddons && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>
                {word}（{mods.length}）<span className="faint">・依存するものは作成時に自動で追加します</span>
              </label>
              {modList.items.length === 0 ? (
                <div className="small faint">まだありません。下で検索して追加してください。</div>
              ) : (
                <div className="row" style={{ gap: 6 }}>
                  {modList.items.map((mod) => (
                    <span
                      key={`${mod.source}:${mod.project}`}
                      ref={modList.ref}
                      className={`tag ${modList.className(mod)}`}
                      inert={!mods.some((entry) => entry === mod)}
                      style={{ gap: 4 }}
                    >
                      {mod.title} <SourceTag source={mod.source} />
                      <button
                        type="button"
                        className="btn ghost small"
                        style={{ padding: '0 4px', minHeight: 0 }}
                        aria-label={`${mod.title} を外す`}
                        onClick={() => setMods(mods.filter((m) => m !== mod))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <AddonSearch
              family={family}
              mcVersion={form.software.mcVersion}
              title={`${word}を検索して追加`}
              stateOf={(hit) =>
                mods.some((m) => m.source === hit.source && (m.project === hit.projectId || m.project === hit.slug))
                  ? 'staged'
                  : undefined
              }
              onAdd={(hit, kind) =>
                setMods([...mods, { kind, project: hit.projectId, title: hit.title, source: hit.source }])
              }
            />
          </div>
        )}
        <h3>起動設定</h3>
        <p>メモリはメモリの設定で変更</p>
        <JvmSettingsFields value={effectiveJvmInput()} onChange={(jvm) => setForm({ ...form, jvm })} />
        <GameSettingsFields value={gameSettings} onChange={setGameSettings} />
        <div className="field">
          <label htmlFor="tpl-props">server.properties の上書き（key=value を 1 行に 1 つ）</label>
          <textarea
            id="tpl-props"
            className="input"
            style={{ height: 90, padding: 8, fontFamily: 'var(--mono)' }}
            value={properties}
            onChange={(e) => setProperties(e.target.value)}
            placeholder={'difficulty=hard\nview-distance=10'}
          />
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={form.proxied && !directOnly}
            disabled={directOnly}
            onChange={(e) => setForm({ ...form, proxied: e.target.checked })}
          />
          Velocity 配下に置く
        </label>
      </form>
    </Dialog>
  );
}

export function Templates() {
  const { canOperate, notify } = useApp();
  const templates = usePoll(() => api3.templates(), 60000);
  const templateList = useAnimatedList(templates.data ?? [], (template) => template.id);
  const [editing, setEditing] = useState<Template | 'new'>();
  // クライアント Mod テンプレートは別のタブで一覧（表）にする。以前のリンク（?clientTemplate=…）もこのタブで開く
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'client' || params.has('clientTemplate') ? 'client' : 'server';
  const changeTab = (value: 'server' | 'client') => {
    const next = new URLSearchParams();
    if (value === 'client') next.set('tab', 'client');
    setParams(next);
  };

  const remove = async (template: Template) => {
    if (!window.confirm(`テンプレート「${template.name}」を削除しますか？作成済みのサーバーには影響しません。`)) return;
    try {
      await api3.deleteTemplate(template.id);
      await templates.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1 data-search-id="templates">テンプレート</h1>
          <p>
            {tab === 'server'
              ? 'サーバー作成時のひな形。ソフト・リソース・Mod・設定をまとめて指定できます。'
              : 'バニラ互換サーバーのプレイヤーにランチャーが入れる Mod の組み合わせです。'}
          </p>
        </div>
        {canOperate && tab === 'server' && (
          <button className="btn primary" onClick={() => setEditing('new')}>
            テンプレートを作成
          </button>
        )}
      </div>
      <div className="template-tabs row" role="tablist" aria-label="テンプレートの種類">
        <button className="btn" role="tab" aria-selected={tab === 'server'} onClick={() => changeTab('server')}>
          サーバー
        </button>
        <button className="btn" role="tab" aria-selected={tab === 'client'} onClick={() => changeTab('client')}>
          クライアント Mod
        </button>
      </div>
      {tab === 'client' && <ClientModTemplatesSection />}
      {tab === 'server' && <ErrorAlert error={templates.error} />}
      {tab === 'server' && templates.data?.length === 0 && <Empty>テンプレートはまだありません。</Empty>}
      {tab === 'server' && (
        <LoadingContent loading={!templates.data && templates.loading}>
          <div className="grid grid-servers">
            {templateList.items.map((template) => (
              <div
                ref={templateList.ref}
                key={template.id}
                className={`card card-body stack ${templateList.className(template)}`}
                inert={!templates.data?.some((entry) => entry.id === template.id)}
                style={{ gap: 8 }}
              >
                <div className="row">
                  <h2>{template.name}</h2>
                  {template.builtin && <span className="tag">同梱</span>}
                </div>
                {template.description && (
                  <p className="small muted" style={{ margin: 0 }}>
                    {template.description}
                  </p>
                )}
                <div className="row small">
                  <span className="tag">{softwareText(template.software)}</span>
                  <span className="muted">
                    {template.resources.cores} コア ・ {formatGb(template.resources.memoryMb)}
                  </span>
                  {template.proxied && <span className="tag">Velocity 配下</span>}
                </div>
                {template.mods.length > 0 && (
                  <div className="small faint">
                    {addonWord(template.software.family)}: {template.mods.map((m) => m.title).join('、')}
                  </div>
                )}
                {template.tasks.length > 0 && (
                  <div className="small faint">
                    スケジュール: {template.tasks.map((t) => `${t.name}（${t.time}）`).join('、')}
                  </div>
                )}
                {canOperate && (
                  <div className="row">
                    <button className="btn small" onClick={() => setEditing(template)}>
                      編集
                    </button>
                    {!template.builtin && (
                      <button className="btn ghost small danger" onClick={() => void remove(template)}>
                        削除
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </LoadingContent>
      )}
      {editing && (
        <TemplateEditor
          initial={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void templates.reload();
          }}
        />
      )}
    </div>
  );
}
